/**
 * Die kleinen Komponenten, die die Routentabelle braucht.
 *
 * Sie standen bis zum 04.09.2026 in `routes.tsx`. Seit die Datei mit `router`
 * einen Nicht-Komponenten-Export trägt, kann Fast Refresh die Komponenten
 * darin nicht mehr auffrischen (`react-refresh/only-export-components`) —
 * getrennte Dateien lösen das, ohne vier Ausnahmen zu setzen.
 */

import { Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSession } from '@entities/session';
import { getDefaultRoute } from '@shared/config';

/**
 * Loading fallback component for lazy-loaded pages
 */
export function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * Redirect /login/educator → /login, preserving query params (oobCode, mode, apiKey)
 * so that existing email links continue to work.
 */
export function EducatorLoginRedirect() {
  const location = useLocation();
  return <Navigate to={`/login${location.search}${location.hash}`} replace />;
}

/**
 * Role-based root redirect.
 * Resolves the landing page dynamically from feature flags:
 * - Educators → /e (own section)
 * - Admins → first route in priority list (flags bypassed)
 * - Employees → first flag-enabled route
 */
export function RootRedirect() {
  const { user } = useSession();
  return <Navigate to={getDefaultRoute(user?.role ?? 'employee')} replace />;
}

/**
 * Suspense-Grenze für die nachgeladenen Seiten.
 *
 * Sie lag bis zum 04.09.2026 innerhalb von `<Routes>`. `createRoutesFromElements`
 * nimmt aber ausschließlich `<Route>`-Kinder, also sitzt sie jetzt in einer
 * pfadlosen Layout-Route ganz außen — dieselbe Stelle im Baum, andere Form.
 */
export function SuspenseLayout() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}
